/*
 * AAIKO favourites.
 *
 * One file, four custom elements, no dependencies:
 *
 *   aaiko-fav-toggle   the heart. Product card, PDP, anywhere.
 *   aaiko-fav-count    the header badge.
 *   aaiko-fav-page     the favourites page: hydration, remove/undo, share.
 *   aaiko-fav-share    a standalone share trigger.
 *
 * ---- Storage model ----
 *
 * localStorage is the source of truth, mirrored into a first-party cookie.
 * The list is namespaced by identity:
 *
 *   aaiko:fav            guest
 *   aaiko:fav:<id>       signed-in customer
 *
 * On sign-in the guest list is merged into the customer list and the guest key
 * is dropped, so a shared device does not hand the next visitor the previous
 * one's selection. The cookie always mirrors whichever list is active; it is
 * capped well under the 4KB cookie limit and is a redundancy layer, never the
 * authority.
 *
 * Server-side, per-customer, cross-device sync is NOT possible from an Online
 * Store theme: aaiko.favourites is a customer metafield and nothing in a theme
 * can write one (no Admin API, no Customer Account API token). The remote
 * adapter below is the seam for that: point window.AAIKO.favourites.endpoint at
 * an app proxy and sync turns on with no other change. Off by default.
 *
 * ---- Rendering ----
 *
 * Liquid cannot read arbitrary query parameters, so the page cannot be server
 * rendered from a ?list= URL. Cards come from sections/aaiko-fav-card.liquid
 * requested per product through the Section Rendering API:
 *
 *   /products/<handle>?section_id=aaiko-fav-card
 *
 * One request per product rather than a batch, deliberately: each fragment is
 * a static, CDN-cacheable URL shared across every visitor, it sidesteps the
 * 20-unique-handle ceiling on all_products, and cards can paint as they land.
 * A small concurrency pool keeps that from becoming a request storm.
 */
(function () {
  'use strict';

  if (window.AaikoFavourites) return;

  var GUEST_KEY = 'aaiko:fav';
  var COOKIE_NAME = 'aaiko_fav';
  var COOKIE_MAX_AGE = 60 * 60 * 24 * 365;
  var COOKIE_MAX_ITEMS = 60;
  var FRAGMENT_CACHE_PREFIX = 'aaiko:favfrag:';
  var MAX_ITEMS = 200;
  var CONCURRENCY = 6;
  var UNDO_MS = 6000;

  var config = (window.AAIKO && window.AAIKO.favourites) || {};
  var customerId = config.customerId ? String(config.customerId) : null;
  var pageUrl = config.pageUrl || '/pages/wishlist';
  var strings = config.strings || {};

  /** @param {string} key @param {Object} [vars] */
  function t(key, vars) {
    var out = strings[key] || '';
    if (!vars) return out;
    Object.keys(vars).forEach(function (name) {
      out = out.split('{{ ' + name + ' }}').join(vars[name]).split('{{' + name + '}}').join(vars[name]);
    });
    return out;
  }

  var reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

  /* ------------------------------------------------------------------ store */

  function activeKey() {
    return customerId ? GUEST_KEY + ':' + customerId : GUEST_KEY;
  }

  /**
   * @param {string} key
   * @returns {{h: string, t: number}[]}
   */
  function readKey(key) {
    try {
      var parsed = JSON.parse(localStorage.getItem(key) || '[]');
      if (!Array.isArray(parsed)) return [];
      return parsed
        .map(function (entry) {
          // v1 of the PDP stub stored bare product ids. They carry no handle,
          // so they cannot be rendered; drop rather than show broken cards.
          if (typeof entry === 'string') return null;
          if (!entry || typeof entry.h !== 'string' || !entry.h) return null;
          return { h: entry.h, t: Number(entry.t) || 0 };
        })
        .filter(Boolean);
    } catch (e) {
      return [];
    }
  }

  /** @param {string} key @param {{h: string, t: number}[]} items */
  function writeKey(key, items) {
    try {
      localStorage.setItem(key, JSON.stringify(items));
    } catch (e) {
      /* private mode or quota: the session stays in memory only */
    }
  }

  /** @param {{h: string, t: number}[]} items */
  function writeCookie(items) {
    var value = items
      .slice(0, COOKIE_MAX_ITEMS)
      .map(function (item) {
        return item.h;
      })
      .join('~');
    var secure = location.protocol === 'https:' ? '; Secure' : '';
    document.cookie =
      COOKIE_NAME +
      '=' +
      encodeURIComponent(value) +
      '; Path=/; Max-Age=' +
      COOKIE_MAX_AGE +
      '; SameSite=Lax' +
      secure;
  }

  function readCookie() {
    var match = document.cookie.match(new RegExp('(?:^|; )' + COOKIE_NAME + '=([^;]*)'));
    if (!match) return [];
    return decodeURIComponent(match[1])
      .split('~')
      .filter(Boolean)
      .map(function (handle) {
        return { h: handle, t: 0 };
      });
  }

  /**
   * Newest first, deduplicated on handle, newest timestamp wins. Used for every
   * merge path: cookie recovery, guest -> account, shared list adoption, remote.
   * @param {...{h: string, t: number}[]} lists
   */
  function merge() {
    var seen = Object.create(null);
    var out = [];
    for (var i = 0; i < arguments.length; i++) {
      var list = arguments[i] || [];
      for (var j = 0; j < list.length; j++) {
        var item = list[j];
        if (!item || !item.h) continue;
        if (seen[item.h]) {
          if (item.t > seen[item.h].t) seen[item.h].t = item.t;
          continue;
        }
        seen[item.h] = { h: item.h, t: item.t };
        out.push(seen[item.h]);
      }
    }
    return out
      .sort(function (a, b) {
        return b.t - a.t;
      })
      .slice(0, MAX_ITEMS);
  }

  var state = [];
  var channel = null;

  function persist(broadcast) {
    writeKey(activeKey(), state);
    writeCookie(state);
    if (broadcast !== false && channel) {
      try {
        channel.postMessage({ handles: state.map(function (i) { return i.h; }) });
      } catch (e) {
        /* channel closed */
      }
    }
    remote.push(state);
    emit();
  }

  function emit() {
    document.dispatchEvent(
      new CustomEvent('aaiko:favourites:change', {
        detail: { handles: state.map(function (i) { return i.h; }), count: state.length },
      })
    );
  }

  /* ------------------------------------------------- remote adapter (opt-in) */

  var remote = {
    endpoint: config.endpoint || null,
    timer: null,
    /** Debounced PUT of the whole list. The endpoint owns identity via session. */
    push: function (items) {
      if (!this.endpoint || !customerId) return;
      var self = this;
      clearTimeout(this.timer);
      this.timer = setTimeout(function () {
        fetch(self.endpoint, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ items: items }),
          keepalive: true,
        }).catch(function () {
          /* offline: localStorage already holds the truth, retry next change */
        });
      }, 600);
    },
    /** @returns {Promise<{h: string, t: number}[]>} */
    pull: function () {
      if (!this.endpoint || !customerId) return Promise.resolve([]);
      return fetch(this.endpoint, { headers: { Accept: 'application/json' } })
        .then(function (res) {
          return res.ok ? res.json() : { items: [] };
        })
        .then(function (data) {
          return Array.isArray(data.items) ? data.items : [];
        })
        .catch(function () {
          return [];
        });
    },
  };

  /* ------------------------------------------------------------ public store */

  var store = {
    /** @returns {string[]} */
    handles: function () {
      return state.map(function (i) {
        return i.h;
      });
    },
    count: function () {
      return state.length;
    },
    /** @param {string} handle */
    has: function (handle) {
      return state.some(function (i) {
        return i.h === handle;
      });
    },
    /** @param {string} handle @returns {boolean} the state after the call */
    toggle: function (handle) {
      if (!handle) return false;
      if (store.has(handle)) {
        store.remove(handle);
        return false;
      }
      store.add(handle);
      return true;
    },
    /**
     * @param {string} handle
     * @param {number} [addedAt] restore an item at its original position
     */
    add: function (handle, addedAt) {
      if (!handle || store.has(handle)) return;
      state = merge([{ h: handle, t: addedAt || Date.now() }], state);
      persist();
    },
    /** @param {string} handle @returns {number} 0 when absent */
    timestampOf: function (handle) {
      var found = state.find(function (i) {
        return i.h === handle;
      });
      return found ? found.t : 0;
    },
    /** @param {string} handle */
    remove: function (handle) {
      var next = state.filter(function (i) {
        return i.h !== handle;
      });
      if (next.length === state.length) return;
      state = next;
      persist();
    },
    /** @param {string[]} handles */
    addMany: function (handles) {
      var now = Date.now();
      var incoming = (handles || [])
        .filter(Boolean)
        .map(function (handle, index) {
          return { h: handle, t: now - index };
        });
      state = merge(incoming, state);
      persist();
    },
    clear: function () {
      state = [];
      persist();
    },
    /** @param {string[]} [handles] defaults to the current list */
    shareUrl: function (handles) {
      var list = handles || store.handles();
      return location.origin + pageUrl + '?list=' + encodeURIComponent(list.join(','));
    },
  };

  /* --------------------------------------------------------------- bootstrap */

  function boot() {
    var mine = readKey(activeKey());

    if (customerId) {
      var guest = readKey(GUEST_KEY);
      if (guest.length) {
        mine = merge(guest, mine);
        try {
          localStorage.removeItem(GUEST_KEY);
        } catch (e) {
          /* ignore */
        }
      }
    }

    // The cookie only rescues a list localStorage lost (ITP eviction, a wiped
    // origin). It never overrides: an empty localStorage with a live cookie is
    // recovery, a shorter localStorage is a deliberate removal.
    if (!mine.length) mine = merge(mine, readCookie());

    state = merge(mine);
    writeKey(activeKey(), state);
    writeCookie(state);

    try {
      channel = new BroadcastChannel('aaiko:fav');
      channel.onmessage = function (event) {
        var handles = (event.data && event.data.handles) || [];
        state = merge(
          handles.map(function (h, i) {
            return { h: h, t: Date.now() - i };
          })
        );
        writeKey(activeKey(), state);
        emit();
      };
    } catch (e) {
      /* no BroadcastChannel: the storage event below still covers other tabs */
    }

    window.addEventListener('storage', function (event) {
      if (event.key !== activeKey()) return;
      state = merge(readKey(activeKey()));
      emit();
    });

    remote.pull().then(function (items) {
      if (!items.length) return;
      var merged = merge(items, state);
      if (merged.length === state.length) return;
      state = merged;
      writeKey(activeKey(), state);
      writeCookie(state);
      emit();
    });
  }

  boot();

  /* ------------------------------------------------------------- fragments */

  var inflight = Object.create(null);

  /**
   * @param {string} handle
   * @returns {Promise<string|null>} card HTML, or null when the product is gone
   */
  function fetchCard(handle) {
    if (inflight[handle]) return inflight[handle];

    var cacheKey = FRAGMENT_CACHE_PREFIX + handle;
    var cached = null;
    try {
      cached = sessionStorage.getItem(cacheKey);
    } catch (e) {
      /* ignore */
    }
    if (cached) return Promise.resolve(cached);

    var url = '/products/' + encodeURIComponent(handle) + '?section_id=aaiko-fav-card';
    inflight[handle] = fetch(url, { headers: { Accept: 'text/html' } })
      .then(function (res) {
        // 404: unpublished, deleted or renamed. Prune it so the list self-heals.
        if (!res.ok) return null;
        return res.text();
      })
      .then(function (html) {
        if (html === null) return null;
        try {
          sessionStorage.setItem(cacheKey, html);
        } catch (e) {
          /* quota: fine, it just refetches */
        }
        return html;
      })
      .catch(function () {
        return null;
      })
      .then(function (result) {
        delete inflight[handle];
        return result;
      });

    return inflight[handle];
  }

  /**
   * Bounded-concurrency map that preserves index order in its callback.
   * @param {string[]} handles
   * @param {(html: string|null, handle: string, index: number) => void} onEach
   * @returns {Promise<void>}
   */
  function loadCards(handles, onEach) {
    var next = 0;
    function worker() {
      if (next >= handles.length) return Promise.resolve();
      var index = next++;
      var handle = handles[index];
      return fetchCard(handle).then(function (html) {
        onEach(html, handle, index);
        return worker();
      });
    }
    var workers = [];
    for (var i = 0; i < Math.min(CONCURRENCY, handles.length); i++) workers.push(worker());
    return Promise.all(workers).then(function () {});
  }

  /* ------------------------------------------------------------- animation */

  /** @param {() => void} mutate */
  function transition(mutate) {
    if (reduceMotion.matches || !document.startViewTransition) {
      mutate();
      return;
    }
    document.startViewTransition(mutate);
  }

  /* --------------------------------------------------------------- elements */

  var LIVE_REGION_ID = 'aaiko-fav-live';

  /* A grid cell. NOT '[data-handle]': every rendered card carries an
     <aaiko-fav-toggle data-handle> of its own, so the bare attribute selector
     matches twice per cell and doubles every count. */
  var CELL = '[data-fav-cell]';

  /** @param {string} message */
  function announce(message) {
    var region = document.getElementById(LIVE_REGION_ID);
    if (!region) {
      region = document.createElement('div');
      region.id = LIVE_REGION_ID;
      region.className = 'aaiko-fav-live';
      region.setAttribute('role', 'status');
      region.setAttribute('aria-live', 'polite');
      document.body.appendChild(region);
    }
    // Re-setting identical text does not re-announce; clear first.
    region.textContent = '';
    window.setTimeout(function () {
      region.textContent = message;
    }, 50);
  }

  class AaikoFavToggle extends HTMLElement {
    connectedCallback() {
      this.button = this.querySelector('button');
      if (!this.button) return;
      this.handle = this.dataset.handle || '';
      // NOT this.title: that is the DOM title property and would render a
      // browser tooltip on every heart.
      this.productTitle = this.dataset.title || '';
      this.button.addEventListener('click', this.onClick);
      document.addEventListener('aaiko:favourites:change', this.sync);
      this.sync();
    }

    disconnectedCallback() {
      if (this.button) this.button.removeEventListener('click', this.onClick);
      document.removeEventListener('aaiko:favourites:change', this.sync);
    }

    onClick = (event) => {
      event.preventDefault();
      event.stopPropagation();
      var added = store.toggle(this.handle);

      if (!reduceMotion.matches) {
        this.button.classList.remove('is-pulsing');
        // reflow, so the animation restarts on a rapid re-toggle
        void this.button.offsetWidth;
        if (added) this.button.classList.add('is-pulsing');
      }

      announce(added ? t('added') : t('removed'));
    };

    sync = () => {
      if (!this.button) return;
      var on = store.has(this.handle);
      this.button.setAttribute('aria-pressed', on ? 'true' : 'false');
      this.button.classList.toggle('is-on', on);
      var label = on ? t('remove_label') : t('add_label');
      if (label) this.button.setAttribute('aria-label', label);
    };
  }

  class AaikoFavCount extends HTMLElement {
    connectedCallback() {
      document.addEventListener('aaiko:favourites:change', this.sync);
      this.sync();
    }

    disconnectedCallback() {
      document.removeEventListener('aaiko:favourites:change', this.sync);
    }

    sync = () => {
      var count = store.count();
      this.textContent = count > 99 ? '99+' : String(count);
      this.toggleAttribute('hidden', count === 0);
    };
  }

  class AaikoFavShare extends HTMLElement {
    connectedCallback() {
      this.button = this.querySelector('button');
      if (!this.button) return;
      this.defaultLabel = this.button.textContent.trim();
      this.button.addEventListener('click', this.onClick);
      document.addEventListener('aaiko:favourites:change', this.sync);
      this.sync();
    }

    disconnectedCallback() {
      if (this.button) this.button.removeEventListener('click', this.onClick);
      document.removeEventListener('aaiko:favourites:change', this.sync);
    }

    sync = () => {
      if (this.button) this.button.disabled = store.count() === 0;
    };

    onClick = () => {
      var url = store.shareUrl();
      var payload = { title: t('share_title'), text: t('share_text'), url: url };

      if (navigator.share && navigator.canShare && navigator.canShare(payload)) {
        navigator.share(payload).catch(function () {
          /* dismissed */
        });
        return;
      }

      var done = () => {
        this.button.textContent = t('share_copied');
        announce(t('share_copied'));
        clearTimeout(this.timer);
        this.timer = setTimeout(() => {
          this.button.textContent = this.defaultLabel;
        }, 2400);
      };

      if (navigator.clipboard && navigator.clipboard.writeText) {
        navigator.clipboard.writeText(url).then(done, function () {
          window.prompt(t('share_title'), url);
        });
      } else {
        window.prompt(t('share_title'), url);
      }
    };
  }

  class AaikoFavPage extends HTMLElement {
    connectedCallback() {
      this.grid = this.querySelector('[data-fav-grid]');
      this.emptyEl = this.querySelector('[data-fav-empty]');
      this.countEl = this.querySelector('[data-fav-count]');
      this.toolsEl = this.querySelector('[data-fav-tools]');
      this.sharedEl = this.querySelector('[data-fav-shared]');
      this.undoEl = this.querySelector('[data-fav-undo]');

      var params = new URLSearchParams(location.search);
      var list = params.get('list');
      this.sharedHandles = list
        ? list.split(',').map(function (h) { return h.trim(); }).filter(Boolean)
        : null;
      this.isShared = Boolean(this.sharedHandles && this.sharedHandles.length);

      this.addEventListener('click', this.onClick);
      document.addEventListener('aaiko:favourites:change', this.onStoreChange);

      if (this.isShared) {
        this.classList.add('is-shared');
        this.render(this.sharedHandles);
      } else {
        this.render(store.handles());
      }
    }

    disconnectedCallback() {
      document.removeEventListener('aaiko:favourites:change', this.onStoreChange);
      clearTimeout(this.undoTimer);
    }

    onStoreChange = () => {
      // A shared view is a snapshot of someone else's list; it must not
      // re-render when the viewer's own list changes underneath it.
      if (this.isShared) {
        this.syncAdoptButton();
        return;
      }
      // Remove and undo mutate the DOM and the store together. Without this
      // guard the store's own change event re-renders the grid from scratch
      // and the node this handler is already inserting lands on top of it.
      if (this.localMutation) return;
      var handles = store.handles();
      var rendered = Array.from(this.grid.querySelectorAll(CELL)).map(function (el) {
        return el.dataset.handle;
      });
      if (handles.length === rendered.length && handles.every(function (h, i) { return h === rendered[i]; })) {
        return;
      }
      this.render(handles);
    };

    /** @param {string[]} handles */
    render(handles) {
      this.setCount(handles.length);

      if (!handles.length) {
        this.grid.innerHTML = '';
        this.setState('empty');
        return;
      }

      this.setState('loading');

      // Skeletons in the drawn card ratio, so the page has its final height
      // before any fragment lands and nothing reflows as cards arrive.
      this.grid.innerHTML = handles
        .map(function (handle) {
          return (
            '<div class="aaiko-fav__cell" data-fav-cell data-handle="' +
            handle.replace(/"/g, '&quot;') +
            '" data-pending><div class="aaiko-fav__skeleton" aria-hidden="true"></div></div>'
          );
        })
        .join('');

      var self = this;
      var missing = [];

      loadCards(handles, function (html, handle) {
        var cell = self.grid.querySelector(CELL + '[data-handle="' + CSS.escape(handle) + '"]');
        if (!cell) return;
        if (html === null) {
          missing.push(handle);
          cell.remove();
          return;
        }
        cell.innerHTML = html;
        cell.removeAttribute('data-pending');
      }).then(function () {
        // Self-healing: products that 404 are gone from the catalogue.
        if (missing.length && !self.isShared) {
          missing.forEach(function (handle) {
            store.remove(handle);
          });
        }
        var remaining = self.grid.querySelectorAll(CELL).length;
        self.setCount(remaining);
        self.setState(remaining ? 'ready' : 'empty');
      });
    }

    /** @param {'loading'|'ready'|'empty'} next */
    setState(next) {
      this.dataset.state = next;
      if (this.emptyEl) this.emptyEl.toggleAttribute('hidden', next !== 'empty');
      // the empty state already says there is nothing here; "0 saved" above it
      // is the same sentence twice
      if (this.countEl) this.countEl.toggleAttribute('hidden', next === 'empty');
      if (this.toolsEl) this.toolsEl.toggleAttribute('hidden', next === 'empty' || this.isShared);
      if (this.sharedEl) this.sharedEl.toggleAttribute('hidden', !this.isShared);
      if (this.isShared) this.syncAdoptButton();
    }

    /** @param {number} count */
    setCount(count) {
      if (!this.countEl) return;
      this.countEl.textContent = count === 1 ? t('count_one') : t('count_other', { count: count });
    }

    syncAdoptButton() {
      var button = this.querySelector('[data-fav-adopt]');
      if (!button || !this.sharedHandles) return;
      var allSaved = this.sharedHandles.every(function (handle) {
        return store.has(handle);
      });
      button.disabled = allSaved;
      button.textContent = allSaved ? t('adopt_done') : t('adopt');
    }

    onClick = (event) => {
      var target = /** @type {HTMLElement} */ (event.target);

      var adopt = target.closest('[data-fav-adopt]');
      if (adopt) {
        store.addMany(this.sharedHandles || []);
        announce(t('adopt_done'));
        this.syncAdoptButton();
        return;
      }

      var undo = target.closest('[data-fav-undo-action]');
      if (undo) {
        this.undoRemove();
        return;
      }

      var remove = target.closest('[data-fav-remove]');
      if (remove) {
        event.preventDefault();
        this.removeCard(remove.closest(CELL));
      }
    };

    /** @param {HTMLElement|null} cell */
    removeCard(cell) {
      if (!cell) return;
      var handle = cell.dataset.handle;
      var index = Array.prototype.indexOf.call(this.grid.children, cell);
      this.pending = {
        handle: handle,
        index: index,
        html: cell.innerHTML,
        t: store.timestampOf(handle),
      };

      var self = this;
      this.localMutation = true;
      transition(function () {
        cell.remove();
        store.remove(handle);
        self.localMutation = false;
        var remaining = self.grid.querySelectorAll(CELL).length;
        self.setCount(remaining);
        if (!remaining) self.setState('empty');
      });

      announce(t('removed'));
      this.showUndo();
    }

    showUndo() {
      if (!this.undoEl) return;
      this.undoEl.hidden = false;
      clearTimeout(this.undoTimer);
      this.undoTimer = setTimeout(() => {
        this.undoEl.hidden = true;
        this.pending = null;
      }, UNDO_MS);
    }

    undoRemove() {
      if (!this.pending) return;
      var pending = this.pending;
      this.pending = null;
      if (this.undoEl) this.undoEl.hidden = true;
      clearTimeout(this.undoTimer);

      var self = this;
      this.localMutation = true;
      transition(function () {
        var cell = document.createElement('div');
        cell.className = 'aaiko-fav__cell';
        cell.setAttribute('data-fav-cell', '');
        cell.dataset.handle = pending.handle;
        cell.innerHTML = pending.html;
        var reference = self.grid.children[pending.index] || null;
        self.grid.insertBefore(cell, reference);
        // after the node is back, so the store's change event finds the DOM
        // already correct; the original timestamp restores its position too
        store.add(pending.handle, pending.t);
        self.localMutation = false;
        self.setState('ready');
        self.setCount(self.grid.querySelectorAll(CELL).length);
      });

      announce(t('restored'));
    }
  }

  if (!customElements.get('aaiko-fav-toggle')) customElements.define('aaiko-fav-toggle', AaikoFavToggle);
  if (!customElements.get('aaiko-fav-count')) customElements.define('aaiko-fav-count', AaikoFavCount);
  if (!customElements.get('aaiko-fav-share')) customElements.define('aaiko-fav-share', AaikoFavShare);
  if (!customElements.get('aaiko-fav-page')) customElements.define('aaiko-fav-page', AaikoFavPage);

  window.AaikoFavourites = store;
})();
